<template>
  <main class="user-add-page">
    <CompanyBreadcrumb :aria-label="$t('users.add_user')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <CompanyTabs animation="uk-animation-fade"
                 class="user-add-tabs-shell"
                 content-id="user-add-switcher"
                 nav-id="userAddOptions"
                 :tabs="addUserTabs"
    >
          <li>
            <template v-if="formErrors.length">
              <div v-for="(error, index) in formErrors" :key="'manual-error-' + index" class="uk-alert-danger" uk-alert>
                <a class="uk-alert-close" uk-close></a>
                <p style="color: red">
                  <i class="uil-exclamation-triangle"></i> {{ error }}
                </p>
              </div>
            </template>
            <div class="card user-add-card">
              <form action="" class="regular-form user-add-form" enctype="multipart/form-data">
                <div class="uk-grid-collapse uk-child-width-1-2@m user-add-form-grid" uk-grid>
                  <div class="user-add-form-col position-relative">
                    <h4>
                      <i class="uil-user-circle"></i>
                      {{ $t("users.information") }}
                    </h4>
                    <div class="user-add-photo">
                      <label class="col-form-label" for="profile-photo-input" @click.prevent="triggerPhotoInput">{{
                        $t("users.photo")
                      }}</label>
                      <div class="user-profile-photo m-auto rounded-circle bg-light">
                        <img id="profile-image-holder" alt="" class="border border-light" src="" />
                        <div class="user-add-photo__overlay">
                          <input id="profile-photo-input"
                                 ref="file"
                                 accept="image/*"
                                 class="d-none"
                                 type="file"
                                 @click="onPhotoInputClick"
                                 @change="onChangeFile"
                          />
                          <button class="user-add-photo__trigger"
                                  type="button"
                                  @click="triggerPhotoInput"
                          >
                            <span aria-hidden="true" class="icon-feather-camera icon-medium text-black"></span>
                            <span class="sr-only">{{ $t("users.photo") }}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="name">{{
                          $t("users.firstname")
                        }}<span class="required">*</span></label>
                        <input id="name" v-model="form.name" class="form-control" name="name" placeholder="" required=""
                          type="text" />
                      </div>
                    </div>

                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="surname">{{
                          $t("users.lastname")
                        }}<span class="required">*</span></label>
                        <input id="surname" v-model="form.surname" class="form-control" name="surname" placeholder=""
                          required="" type="text" />
                      </div>
                    </div>

                    <div v-if="!shouldHideEmailField" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="mail">{{
                          $t("general.email")
                        }}<span class="required">*</span></label>
                        <input id="mail" v-model="form.email" class="form-control" name="mail" placeholder="" required=""
                          type="email" />
                      </div>
                    </div>

                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label">{{ $t("gender.gender") }}</label>
                        <multiple-select-input :allow-empty="true"
                                               :model.sync="selectedGender"
                                               :searchable="false"
                                               :show-labels="false"
                                               :values="genderOptions"
                        />
                      </div>
                    </div>

                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="phone">{{ $t("general.phone") }}
                          <span v-if="form.login_type_id === 2 || form.authentication_type_id === 3"
                            class="required">*</span>
                        </label>
                        <div class="uk-position-relative w-100">
                          <PhoneInput v-model="maskPhone" :country-phone-code="form.country_phone_code"
                            name="user_phone" />
                        </div>
                      </div>
                    </div>

                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="sicil">{{
                          $t("users.reg_no")
                        }}<span v-if="form.login_type_id === 4" class="required">*</span></label>
                        <input id="sicil" v-model="form.registration_number" class="form-control" name="sicil"
                          placeholder="" type="text" />
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="sicil">{{ $t('general.national_id') }}<span v-if="form.login_type_id === 3"
                            class="required">*</span></label>
                        <input id="national_id" v-model="form.national_id" class="form-control" name="national_id"
                          placeholder="" type="text" />
                      </div>
                    </div>

                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="dob">{{
                          $t("users.dob")
                        }}</label>
                        <input id="dob" v-model="form.dob" class="form-control" name="dob" placeholder="" required=""
                          type="date" />
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label">{{ $t("profile.login_type") }}
                          <info-tooltip v-if="lockedLoginTypeId" :helper-text="$t('general.login_type_locked_by_company')" />
                        </label>
                        <multiple-select-input :allow-empty="false"
                                               :model.sync="selectedLoginType"
                                               :searchable="false"
                                               :show-labels="false"
                                               :values="loginTypeOptions"
                                               :disabled="!!lockedLoginTypeId"
                        />
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label">{{ $t("profile.authentication_type") }}</label>
                        <multiple-select-input :allow-empty="false"
                                               :model.sync="selectedAuthType"
                                               :searchable="false"
                                               :show-labels="false"
                                               :values="authentication_types"
                        />
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label">{{ $t("general.notification_method") }}</label>
                        <multiple-select-input :allow-empty="false"
                                               :model.sync="selectedNotification"
                                               :searchable="false"
                                               :show-labels="false"
                                               :values="notificationOptions"
                        />
                      </div>
                    </div>

                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label">{{ $t("general.language") }}</label>
                        <multiple-select-input :allow-empty="true"
                                               :model.sync="selectedLanguage"
                                               :show-labels="false"
                                               :values="languageOptions"
                        />
                      </div>
                    </div>

                    <div class="form-group row mt-4">
                      <div class="col-md-12">
                        <h4>
                          <i class="uil-user-circle"></i>
                          {{ $t("users.user_add_to_group") }}
                        </h4>
                        <multiple-select-input :allow-empty="true"
                                               :model.sync="userGroup"
                                               :multiple="true"
                                               :searchable="true"
                                               :show-labels="false"
                                               :values="formattedUserGroup"
                        />
                      </div>
                    </div>
                    <hr />
                    <div class="form-group row mt-3 input-checkbox">
                      <div class="col-md-12">
                        <h4>
                          <i class="uil-asterisk"></i>
                          {{ $t("users.user_password") }}
                        </h4>
                        <div class="user-add-checkbox">
                          <input id="user-password" v-model="passwordStatus" checked type="checkbox" />
                          <label for="user-password">{{
                            $t("users.user_password_auto")
                          }}</label>
                        </div>
                        <label class="col-form-label" for="pass">{{
                          $t("general.password")
                        }}<span class="required">*</span></label>
                        <input id="pass" v-model="form.password" class="form-control" disabled name="pass" placeholder=""
                          required="" type="password" />
                      </div>
                    </div>
                  </div>
                  <div class="user-add-form-col">
                    <h4 v-if="$isSuperAdmin">
                      <i class="uil-bag"></i>
                      {{ $t('general.authorization') }}
                    </h4>

                    <div class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="institution">
                          {{ $t('general.role_definition') }}
                          <span class="required">*</span>
                        </label>
                        <multiple-select-input :allow-empty="true"
                                               :model.sync="selectedRoles"
                                               :multiple="true"
                                               :searchable="true"
                                               :show-labels="false"
                                               :values="roles"
                        />
                      </div>
                    </div>
                    <div class="form-group row" v-if="isHierarchy">
                      <div class="col-md-12">
                        <label class="col-form-label" for="institution">
                          {{ $t('general.linked_manager') }}
                          <span class="required">*</span>
                        </label>
                        <multiple-select-input :allow-empty="true"
                                               :model.sync="selectedUser"
                                               :searchable="true"
                                               :show-labels="false"
                                               :values="userOptions"
                        />
                      </div>
                    </div>

                    <h4 v-if="!$isSuperAdmin">
                      <i class="uil-bag"></i>
                      {{ $t("users.business_information") }}
                    </h4>

                    <div v-if="form.company_id" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="position">{{
                          $t("users.mission")
                        }}</label>
                        <multiple-select-input :model.sync="position" :required="false" :searchable="true"
                          :showLabels="false" :tag-placeholder="$t('general.add_tag_mission')" :taggable="true"
                          :values="formattedPosition" @addTag="addTagToMission" @select="form.position_id = $event.id" />
                      </div>
                    </div>

                    <div v-if="form.company_id" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="title">{{
                          $t("users.title")
                        }}</label>
                        <multiple-select-input :model.sync="title" :required="false" :searchable="true"
                          :showLabels="false" :tag-placeholder="$t('general.add_tag_title')" :taggable="true"
                          :values="formattedTitle" @addTag="addTagToTitles" @select="form.title_id = $event.id" />
                      </div>
                    </div>

                    <div v-if="form.company_id" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="region">
                          {{ $t("users.area") }}</label>
                        <multiple-select-input :model.sync="region" :required="false" :searchable="true"
                          :showLabels="false" :tag-placeholder="$t('general.add_tag_region')" :taggable="true"
                          :values="formattedRegion" @addTag="addTagToRegion" @select="form.region_id = $event.id" />
                      </div>
                    </div>

                    <div v-if="form.company_id" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="department">{{
                          $t("users.department")
                        }}</label>
                        <multiple-select-input :model.sync="department" :required="false" :searchable="true"
                          :showLabels="false" :tag-placeholder="$t('general.add_tag_department')" :taggable="true"
                          :values="formattedDepartment" @addTag="addTagToDepartment"
                          @select="form.department_id = $event.id" />
                      </div>
                    </div>

                    <div v-if="form.company_id && $hasPermission('read_branch')" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label" for="branch">
                          {{ $t("users.branch") }}</label>
                        <multiple-select-input :model.sync="branch" :required="false" :searchable="true"
                          :showLabels="false" :tag-placeholder="$t('general.add_tag_branch')" :taggable="true"
                          :values="formattedBranch" @addTag="addTagToBranch" @select="form.branch_id = $event.id" />
                      </div>
                    </div>

                    <div v-if="form.company_id" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label">{{ $t('general.company') }}</label>
                        <CorporateSelect v-model="corporate" :company-id="form.company_id" :multiple="false" />
                      </div>
                    </div>

                    <div v-if="integrationOptions.length" class="form-group row">
                      <div class="col-md-12">
                        <label class="col-form-label">{{ $t('general.integrations') || 'Entegrasyonlar' }}</label>
                        <multiple-select-input :allow-empty="true"
                                               :model.sync="selectedIntegrations"
                                               :multiple="true"
                                               :searchable="true"
                                               :show-labels="false"
                                               :values="integrationOptions"
                                               label="title"
                                               track-by="type"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="uk-card-footer user-add-form-footer">
                  <button :disabled="isSubmitting"
                          class="uk-button uk-button-primary user-add-save"
                          type="button"
                          @click="formSubmit"
                  >
                    <i v-if="isSubmitting" class="uil-spinner" uk-spinner="ratio: 0.8"></i>
                    {{ isSubmitting ? `${$t('general.saving')}...` : $t('general.save') }}
                  </button>
                </div>
              </form>
            </div>
          </li>
          <li>
            <user-from-excel-uploader :sample-excel-endpoint="'/api/user/export-template'" />
          </li>
          <li>
            <div class="user-add-hr-panel">
              <i aria-hidden="true" class="uil-chat-bubble-user"></i>
              <p>{{ $t("users.hr_integration") }}</p>
            </div>
          </li>
    </CompanyTabs>
  </main>
</template>

<script>
import store from "@/core/services";
import axios from "axios";
import company_module, {
  BASE_URL as COMPANY_BASE_URL,
  GET_ITEMS as GET_INSTITUTIONS,
  MODULE_NAME as COMPANY_MODULE,
} from "@/core/services/store/company.module";

import user_module, {
  BASE_URL as USER_BASE_URL,
  CREATE_ITEM as CREATE_USER,
  ERROR as USER_ERRORS,
  MODULE_NAME as USER_MODULE,
  SUCCESS,
} from "@/core/services/store/user.module";

import user_group_module, {
  BASE_URL as USER_GROUP_BASE_URL,
  GET_ITEMS as GET_USER_GROUPS,
  ITEMS as USER_GROUPS,
  MODULE_NAME as USER_GROUP_MODULE,
} from "@/core/services/store/user_group.module";

import position_module, {
  BASE_URL as POSITION_BASE_URL,
  CREATE_ITEM as POSITION_CREATE_ITEM,
  ERROR as POSITION_ERROR,
  GET_ITEMS as GET_POSITIONS,
  ITEMS as POSITIONS,
  MODULE_NAME as POSITION_MODULE,
} from "@/core/services/store/position.module";

import title_module, {
  BASE_URL as TITLE_BASE_URL,
  CREATE_ITEM as TITLE_CREATE_ITEM,
  GET_ITEMS as GET_TITLES,
  ITEMS as TITLES,
  MODULE_NAME as TITLE_MODULE,
} from "@/core/services/store/title.module";

import region_module, {
  BASE_URL as REGION_BASE_URL,
  CREATE_ITEM as REGION_CREATE_ITEM,
  GET_ITEMS as GET_REGIONS,
  ITEMS as REGIONS,
  MODULE_NAME as REGION_MODULE,
} from "@/core/services/store/region.module";

import department_module, {
  BASE_URL as DEPARTMENT_BASE_URL,
  CREATE_ITEM as DEPARTMENT_CREATE_ITEM,
  GET_ITEMS as GET_DEPARTMENTS,
  ITEMS as DEPARTMENTS,
  MODULE_NAME as DEPARTMENT_MODULE,
} from "@/core/services/store/department.module";

import branch_module, {
  BASE_URL as BRANCH_BASE_URL,
  CREATE_ITEM as BRANCH_CREATE_ITEM,
  GET_ITEMS as GET_BRANCHS,
  ITEMS as BRANCHS,
  MODULE_NAME as BRANCH_MODULE,
} from "@/core/services/store/branch.module";

import InfoTooltip from "@/assets/components/widget/InfoTooltip";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import PhoneInput from "@/assets/components/inputs/PhoneInput";
import CorporateSelect from "@/view/components/select/CorporateSelect.vue";
import UserFromExcelUploader from "@/view/company/User/components/UserFromExcelUploader.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import CompanyTabs from "@/view/company/components/tabs/CompanyTabs.vue";
import { isHierarchy } from "@/core/services/store.js";
import i18nService from "@/core/services/i18n.service.js";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";
const _USER_GROUP_MODULE_NAME = USER_GROUP_MODULE;
const _COMPANY_MODULE_NAME = COMPANY_MODULE;
const _POSITION_MODULE = POSITION_MODULE;
const _TITLE_MODULE = TITLE_MODULE;
const _REGION_MODULE = REGION_MODULE;
const _DEPARTMENT_MODULE = DEPARTMENT_MODULE;
const _BRANCH_MODULE = BRANCH_MODULE;
const _USER_MODULE = USER_MODULE;

export default {
  name: "UserAdd",
  components: {
    MultipleSelectInput,
    InfoTooltip,
    PhoneInput,
    CorporateSelect,
    UserFromExcelUploader,
    CompanyBreadcrumb,
    CompanyTabs,
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_COMPANY_MODULE_NAME, company_module);
    registerStoreModule(_USER_GROUP_MODULE_NAME, user_group_module);
    registerStoreModule(_POSITION_MODULE, position_module);
    registerStoreModule(_TITLE_MODULE, title_module);
    registerStoreModule(_REGION_MODULE, region_module);
    registerStoreModule(_DEPARTMENT_MODULE, department_module);
    registerStoreModule(_BRANCH_MODULE, branch_module);
    registerStoreModule(_USER_MODULE, user_module);
  },
  data() {
    return {
      users: [],
      loginTypeCompany: null,
      maskPhone: '',
      passwordStatus: true,
      login_types: [],
      genders: [],
      languages: [],
      authentication_types: [],
      notification_methods: [
        { name: this.$t("general.notify_via_email"), value: "email" },
        { name: this.$t("general.notify_via_sms"), value: "sms" },
      ],
      userGroup: [],
      position: {
        id: null,
        name: null,
      },
      title: {
        id: null,
        name: null,
      },
      region: {
        id: null,
        name: null,
      },
      department: {
        id: null,
        name: null,
      },
      branch: {
        id: null,
        name: null,
      },
      corporate: null,
      form: {
        name: "",
        surname: "",
        email: "",
        phone: "",
        dob: "",
        login_type_id: 1,
        gender_id: null,
        authentication_type_id: 2,
        registration_number: "",
        user_group: null,
        company_id: null,
        branch_id: null,
        department_id: null,
        region_id: null,
        title_id: null,
        position_id: null,
        corporate_id: null,
        password: "",
        photo: null,
        national_id: "",
        country_phone_code: '',
        notification_method: "email",
        language_id: null,
      },
      formErrors: [],
      roles: [],
      selectedRoles: [],
      selectedUser: null,
      isSubmitting: false,
      integrationOptions: [],
      selectedIntegrations: [],
      _waitingForFile: false,
    };
  },
  computed: {
    lockedLoginTypeId() {
      const company = this.loginTypeCompany;
      if (!company || Number(company.is_allowed_login_type) || !company.login_type_id) {
        return null;
      }
      return company.login_type_id;
    },
    loginTypeOptions() {
      if (!this.lockedLoginTypeId) {
        return this.login_types;
      }
      return (this.login_types || []).filter((item) => item.id === this.lockedLoginTypeId);
    },
    addUserTabs() {
      return [
        { key: "manual", icon: "uil-user-plus", label: this.$t("users.add_manuel") },
        { key: "excel", icon: "uil-table", label: this.$t("users.add_to_excel") },
        { key: "hr", icon: "uil-chat-bubble-user", label: this.$t("users.hr_integration") },
      ];
    },
    genderOptions() {
      return (this.genders || []).map((item) => ({
        id: item.id,
        name: this.$t("gender." + item.key),
      }));
    },
    selectedGender: {
      get() {
        return this.genderOptions.find((item) => item.id === this.form.gender_id) || null;
      },
      set(value) {
        this.form.gender_id = value ? value.id : null;
      },
    },
    selectedLoginType: {
      get() {
        return (this.login_types || []).find((item) => item.id === this.form.login_type_id) || null;
      },
      set(value) {
        if (value) {
          this.form.login_type_id = value.id;
        }
      },
    },
    selectedAuthType: {
      get() {
        return (this.authentication_types || []).find((item) => item.id === this.form.authentication_type_id) || null;
      },
      set(value) {
        if (value) {
          this.form.authentication_type_id = value.id;
        }
      },
    },
    notificationOptions() {
      return (this.notification_methods || []).map((item) => ({
        id: item.value,
        name: item.name,
      }));
    },
    selectedNotification: {
      get() {
        return this.notificationOptions.find((item) => item.id === this.form.notification_method) || null;
      },
      set(value) {
        if (value) {
          this.form.notification_method = value.id;
        }
      },
    },
    languageOptions() {
      return this.languages || [];
    },
    selectedLanguage: {
      get() {
        return this.languageOptions.find((item) => item.id === this.form.language_id) || null;
      },
      set(value) {
        this.form.language_id = value && value.id != null ? Number(value.id) : null;
      },
    },
    userOptions() {
      return (this.users || []).map((user) => ({
        ...user,
        name: `${user.name || ""} ${user.surname || ""}`.trim(),
      }));
    },
    breadcrumbItems() {
      return [
        { name: this.$t("users.users") },
        { name: this.$t("users.add_user") },
      ];
    },
    isHierarchy() {
      return isHierarchy.value;
    },
    shouldHideEmailField() {
      return Number(this.form.company_id) === 489;
    },
    errors() {
      //tag error + user error
      return store.getters[_USER_MODULE + "/" + USER_ERRORS] || store.getters[POSITION_MODULE + "/" + POSITION_ERROR];
    },
    success() {
      return store.getters[_USER_MODULE + "/" + SUCCESS];
    },
    userInfo: {
      get: function () {
        return JSON.parse(this.secureLocalStorage("user"));
      },
      set: function (newValue) {
      },
    },
    branchs: {
      get() {
        return store.getters[BRANCH_MODULE + "/" + BRANCHS];
      },
      set(value) {
      },
    },
    departments: {
      get() {
        return store.getters[DEPARTMENT_MODULE + "/" + DEPARTMENTS];
      },
      set(value) {
      },
    },
    regions: {
      get() {
        return store.getters[REGION_MODULE + "/" + REGIONS];
      },
      set(value) {
      },
    },
    titles: {
      get() {
        return store.getters[TITLE_MODULE + "/" + TITLES];
      },
      set(value) {
      },
    },
    positions: {
      get() {
        return store.getters[POSITION_MODULE + "/" + POSITIONS];
      },
      set(value) {
      },
    },
    user_groups: {
      get() {
        return store.getters[USER_GROUP_MODULE + "/" + USER_GROUPS];
      },
      set(value) {
      },
    },
    formattedBranch() {
      let results = [
        {
          value: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.branchs === null || !this.branchs.length) return [];

      this.branchs.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedDepartment() {
      let results = [
        {
          value: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.departments === null || !this.departments.length) return [];

      this.departments.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedRegion() {
      let results = [
        {
          value: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.regions === null || !this.regions.length) return [];

      this.regions.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedUserGroup() {
      let groups = [];
      if (this.user_groups === null || !this.user_groups.length) return [];
      this.user_groups.forEach((item) => {
        if (item.status === 1 || item.status) {
          groups.push({
            id: item.id,
            name: item.name,
          });
        }
      });
      return groups;
    },
    formattedPosition() {
      let positions = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.positions === null || !this.positions.length) return [];

      this.positions.forEach((item) => {
        positions.push({
          id: item.id,
          name: item.name,
        });
      });
      return positions;
    },
    formattedTitle() {
      let titles = [
        {
          value: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.titles === null || !this.titles.length) return [];

      this.titles.forEach((item) => {
        titles.push({
          id: item.id,
          name: item.name,
        });
      });
      return titles;
    },
  },
  methods: {
    generateMadParfumEmail() {
      const randomKey = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      return `${randomKey}@madparfum.com`;
    },
    ensureMadParfumEmail() {
      if (!this.shouldHideEmailField) {
        return;
      }

      if (!this.form.email || this.form.email.trim().length === 0) {
        this.form.email = this.generateMadParfumEmail();
      }
    },
    onPhotoInputClick(event) {
      if (!bridge.isNative) {
        return;
      }
      event.preventDefault();
      this._waitingForFile = true;
      native.pickFile("image/*");
    },
    triggerPhotoInput() {
      if (bridge.isNative) {
        this._waitingForFile = true;
        native.pickFile("image/*");
        return;
      }
      const input = this.$refs.file;
      if (!input) {
        return;
      }
      input.value = null;
      input.click();
    },
    applyPhotoFile(file) {
      if (!file) {
        return;
      }

      this.form.photo = file;
      const blob = URL.createObjectURL(file);
      const imageHolder = $("#profile-image-holder");
      imageHolder.attr("src", blob);
    },
    onChangeFile() {
      this.applyPhotoFile(this.$refs.file?.files?.[0]);
    },
    getRoles() {
      axios.get(`${'api/roles'}`, {
        headers: this.apiHeaders
      }).then((result) => {
        this.roles = result.data?.global;
        result.data?.company.forEach(item => {
          this.roles.push(item);
        });
      });
    },
    addTagToMission(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(POSITION_MODULE + "/" + POSITION_CREATE_ITEM, {
          url: POSITION_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getPositions().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.position = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.position_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          console.error('Position creation error:', error);
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToTitles(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(TITLE_MODULE + "/" + TITLE_CREATE_ITEM, {
          url: TITLE_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getTitles().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.title = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.title_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          console.error('Position creation error:', error);
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToRegion(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(REGION_MODULE + "/" + REGION_CREATE_ITEM, {
          url: REGION_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getRegions().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.region = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.region_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          console.error('Position creation error:', error);
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToDepartment(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(DEPARTMENT_MODULE + "/" + DEPARTMENT_CREATE_ITEM, {
          url: DEPARTMENT_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getDepartments().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.department = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.department_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          console.error('Position creation error:', error);
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToBranch(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(BRANCH_MODULE + "/" + BRANCH_CREATE_ITEM, {
          url: BRANCH_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getBranchs().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.branch = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.branch_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          console.error('Position creation error:', error);
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    createFromTagData(tag) {
      let content = new FormData();
      content.append("status", 1);
      content.append("name", tag.name);
      return content
    },

    // Form validation fonksiyonu
    validateNewUserAddForm(form) {
      let self = this;
      self.formErrors = [];

      if (self.maskPhone) {
        form.country_phone_code = self.maskPhone.substring(0, self.maskPhone.indexOf(' ')).substring(1);
        form.phone = self.maskPhone.length > 5 ? self.maskPhone.substring(self.maskPhone.indexOf(' ') + 1).replaceAll(' ', '') : null;
      }

      if (!form.name || form.name.trim().length === 0) {
        self.formErrors.push(self.$t("users.required.firstname"));
      }

      if (!form.surname || form.surname.trim().length === 0) {
        self.formErrors.push(self.$t("users.required.lastname"));
      }

      if (!self.shouldHideEmailField && (!form.email || form.email.trim().length === 0)) {
        self.formErrors.push(self.$t("users.required.email"));
      }

      let hasPhoneSelectedForAnyType = form.authentication_type_id === 3 || form.login_type_id === 2;
      let isPhoneRequired = hasPhoneSelectedForAnyType && (!form.phone || form.phone.length === 0);

      if (isPhoneRequired) {
        self.formErrors.push(self.$t("users.required.phone"));
      }

      // Telefon formatı kontrolü
      if (isPhoneRequired && form.phone) {
        if (form.phone.length !== 10) {
          self.formErrors.push(self.$t("general.phone_must_be_10_digits"));
        } else if (Number.isInteger(form.phone)) {
          self.formErrors.push(self.$t("general.phone_digits_only"));
        }
      }

      if (form.login_type_id === 2 && (!form.phone || form.phone === "")) {
        self.formErrors.push(self.$t("users.required.phone"));
      }

      if (form.login_type_id === 3 && (!form.national_id || form.national_id === "")) {
        self.formErrors.push(self.$t("general.required_national_id"));
      }

      if (form.login_type_id === 4 && (!form.registration_number || form.registration_number === "")) {
        self.formErrors.push(self.$t("users.required.reg_no"));
      }

      // Eğer hata varsa, hataları göster
      if (self.formErrors.length > 0) {
        self.$nextTick(() => {
          self.scrollToTop();
        });
        return false;
      }

      return true;
    },
    formSubmit() {
      let self = this;

      // Eğer zaten submit işlemi çalışıyorsa, yeni submit'i engelle
      if (self.isSubmitting) {
        return false;
      }

      let form = self.form;
      self.ensureMadParfumEmail();

      // Form validation yap
      if (!self.validateNewUserAddForm(form)) {
        return false;
      }

      // Submit işlemi başladı
      self.isSubmitting = true;

      let form_data = new FormData();
      form_data.append("name", form.name);
      form_data.append("surname", form.surname);
      form_data.append("email", form.email);
      form_data.append("phone", form.phone);
      form_data.append("dob", form.dob);
      form_data.append("registration_number", form.registration_number);
      form_data.append("national_id", form.national_id);
      form_data.append("country_phone_code", form.country_phone_code);
      form_data.append("user_group", form.user_group);
      form_data.append("company_id", form.company_id);
      form_data.append("branch_id", form.branch_id);
      form_data.append("department_id", form.department_id);
      form_data.append("region_id", form.region_id);
      form_data.append("title_id", form.title_id);
      form_data.append("position_id", form.position_id);
      form_data.append("corporate_id", form.corporate_id);
      form_data.append("password", form.password);
      form_data.append("login_type_id", form.login_type_id);
      form_data.append("gender_id", form.gender_id);
      form_data.append("authentication_type_id", form.authentication_type_id);
      form_data.append("notification_method", form.notification_method);
      const languageId = Number(form.language_id);
      if (Number.isInteger(languageId) && languageId > 0) {
        form_data.append("language_id", languageId);
      }
      form_data.append("photo", form.photo);
      if (self.userGroup && self.userGroup.length > 0) {
        self.userGroup.forEach(item => {
          form_data.append("group_ids[]", item.id);
        });
      } else {
        form_data.append("group_ids[]", "");
      }
      form_data.append("parent_id", self.selectedUser?.id ?? null);
      if (self.selectedRoles && self.selectedRoles.length > 0) {
        self.selectedRoles.forEach(item => {
          form_data.append("role_ids[]", item.id);
        });
      } else {
        form_data.append("role_ids[]", "");
      }
      if (self.selectedIntegrations && self.selectedIntegrations.length > 0) {
        self.selectedIntegrations.forEach((item) => {
          form_data.append("integration_tags[]", item.type);
        });
      }
      return this.$store.dispatch(USER_MODULE + "/" + CREATE_USER, {
        url: USER_BASE_URL,
        contents: form_data,
      }).catch((error) => {
        self.formErrors = [];

        const data = error?.response?.data || error;

        if (data?.errors) {
          Object.values(data.errors).forEach((messages) => {
            if (Array.isArray(messages)) {
              self.formErrors.push(...messages);
            } else {
              self.formErrors.push(messages);
            }
          });
        } else if (data?.message) {
          self.formErrors.push(data.message);
        } else if (typeof data === "string") {
          self.formErrors.push(data);
        } else {
          self.formErrors.push(self.$t("general.error_occurred"));
        }

        self.$nextTick(() => {
          self.scrollToTop();
        });
      })
        .finally(() => {
          self.isSubmitting = false;
        });
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
    getBranchs() {
      return this.$store.dispatch(BRANCH_MODULE + "/" + GET_BRANCHS, {
        url: BRANCH_BASE_URL,
        filters: {
          company_id: this.form.company_id,
        },
      });
    },
    getDepartments() {
      return this.$store.dispatch(DEPARTMENT_MODULE + "/" + GET_DEPARTMENTS, {
        url: DEPARTMENT_BASE_URL,
        filters: {
          company_id: this.form.company_id,
        },
      });
    },
    getRegions() {
      return this.$store.dispatch(REGION_MODULE + "/" + GET_REGIONS, {
        url: REGION_BASE_URL,
        filters: {
          company_id: this.form.company_id,
        },
      });
    },
    getTitles() {
      return this.$store.dispatch(TITLE_MODULE + "/" + GET_TITLES, {
        url: TITLE_BASE_URL,
        filters: {
          company_id: this.form.company_id,
        },
      });
    },
    getPositions() {
      return this.$store.dispatch(POSITION_MODULE + "/" + GET_POSITIONS, {
        url: POSITION_BASE_URL,
        filters: {
          company_id: this.form.company_id,
        },
      });
    },
    getInstitutions() {
      this.$store.dispatch(COMPANY_MODULE + "/" + GET_INSTITUTIONS, {
        url: COMPANY_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    getLoginTypeCompany() {
      axios.get(`api/company/self`, {
        headers: this.apiHeaders,
      }).then((result) => {
        this.loginTypeCompany = result.data;
      });
    },
    getLoginTypes() {
      let types = [];
      axios
        .get(`api/user/login-types`, {
          headers: this.apiHeaders,
        })
        .then((result) => {
          result.data.forEach((data) => {
            types.push({
              id: data.id,
              name: data.name,
            });
          });
        });
      this.login_types = types;
    },
    getGenders() {
      axios
        .get(`api/genders`, { headers: this.apiHeaders })
        .then((result) => {
          this.genders = result.data.data.map(item => ({
            id: item.id,
            key: item.key,
            label: item.label,
          }));
        })
    },
    getSupportedLanguageCodes() {
      return (i18nService.languages || []).map((item) => String(item.lang || "").toLowerCase());
    },
    normalizeLanguageCode(item) {
      return String(item?.code || item?.language || item?.lang || "").toLowerCase();
    },
    getLanguages() {
      const supportedCodes = this.getSupportedLanguageCodes();
      axios
        .get(`api/localization`, { headers: this.apiHeaders })
        .then((result) => {
          const backendLanguages = result?.data?.languages || [];
          this.languages = backendLanguages
            .filter((item) => supportedCodes.includes(this.normalizeLanguageCode(item)))
            .map((item) => ({
              ...item,
              id: Number(item.id),
            }))
            .filter((item) => Number.isInteger(item.id) && item.id > 0);
        });
    },

    getAuthenticationTypes() {
      let types = [];
      axios
        .get(`api/user/authentication-types`, {
          headers: this.apiHeaders,
        })
        .then((result) => {
          result.data.forEach((data) => {
            types.push({
              id: data.id,
              name: data.name,
            });
          });
        });
      this.authentication_types = types;
    },
    getIntegrationOptions() {
      axios.get('api/widgets', {
        params: { with_user_custom_list: 1 },
        headers: this.apiHeaders,
      }).then((result) => {
        const options = Array.isArray(result.data) ? [...result.data] : [];
        if (!options.some(option => option.type === 'gofluent')) {
          options.push({ type: 'gofluent', title: 'Go Fluent' });
        }
        this.integrationOptions = options;
        this.selectedIntegrations = [...options];
      }).catch(() => {
        const goFluentOption = { type: 'gofluent', title: 'Go Fluent' };
        this.integrationOptions = [goFluentOption];
        this.selectedIntegrations = [goFluentOption];
      });
    },
    getUserGroups() {
      this.$store.dispatch(USER_GROUP_MODULE + "/" + GET_USER_GROUPS, {
        url: USER_GROUP_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    getValues() {
      let temp = this.form.company_id;
      this.form.company_id = null;
      this.form.company_id = temp;

      this.getPositions();
      this.getTitles();
      this.getRegions();
      this.getDepartments();
      this.getBranchs();

      this.form.position_id = "";
      this.form.title_id = "";
      this.form.region_id = "";
      this.form.department_id = "";
      this.form.branch_id = "";
    },
    getUsers() {
      axios.get(`${'api/user'}`, {
        headers: this.apiHeaders
      }).then((result) => {
        this.users = result.data;
      });
    }
  },
  mounted() {
    this.getInstitutions();
    this.getLoginTypes();
    this.getLoginTypeCompany();
    this.getGenders();
    this.getLanguages();
    this.getAuthenticationTypes();
    this.getUserGroups();
    this.getRoles();
    this.getUsers();
    this.getIntegrationOptions();
    if (!this.$isSuperAdmin) {
      this.form.company_id = this.userInfo.company_id;
      this.getValues();
    }

    let activeTab = this.$route.params.type;

    if (activeTab) {
      setTimeout(() => {
        $("#userAddOptions li:nth-child(" + Number(activeTab) + ") i").trigger(
          "click"
        );
      }, 100);
    }

    if (bridge.isNative) {
      this._unsubscribePickFile = bridge.on("PICK_FILE_RESULT", (payload) => {
        if (!this._waitingForFile) return;
        this._waitingForFile = false;

        if (!payload.success || !payload.file) return;
        const { name, type, base64 } = payload.file;

        const byteString = atob(base64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        this.applyPhotoFile(new File([ab], name, { type }));
      });
    }
  },
  beforeDestroy() {
    if (this._unsubscribePickFile) {
      this._unsubscribePickFile();
    }
  },
  watch: {
    lockedLoginTypeId(value) {
      if (value) {
        this.form.login_type_id = value;
      }
    },
    "form.authentication_type_id": {
      immediate: true,
      handler(newValue) {
        if (newValue === 3) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
    },
    "form.login_type_id": {
      immediate: true,
      handler(newValue) {
        if (newValue === 2) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
    },
    corporate(value) {
      this.form.corporate_id = value ? value.id : null;
    },
    success: function (val) {
      if (val) {
        this.isSubmitting = false;
        this.$router.push({
          name: "company.user.list",
          params: { succesMessage: this.$t("general.create_success") },
        });
      }
    },
    errors: function (val) {
      if (val) {
        this.isSubmitting = false;
        this.formErrors.push(val);
        this.$nextTick(() => {
          this.scrollToTop();
        });
      }
    },
    passwordStatus(value) {
      if (value) {
        $("#pass").prop("disabled", true);
        this.form.password = "12345678";
      } else {
        $("#pass").prop("disabled", false);
      }
      this.form.password = "";
    },
  },
};
</script>
<style scoped>
.user-add-page {
  --user-add-primary: var(--primaryColor, #3e416d);
  --user-add-text: #1e293b;
  --user-add-muted: #64748b;
  --user-add-border: #e2e8f0;
  min-width: 0;
}

.user-add-page ::v-deep .company-tabs__content > li {
  padding: 0;
}

.user-add-card {
  overflow: visible;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.user-add-hr-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  padding: 32px 16px;
  color: var(--user-add-muted);
  text-align: center;
}

.user-add-hr-panel i {
  margin-bottom: 8px;
  font-size: 40px;
}

.user-add-form-col {
  padding: 32px;
}

.user-add-form-col + .user-add-form-col {
  border-left: 1px solid var(--user-add-border);
}

.user-add-form-grid,
.user-add-form-col {
  min-width: 0;
}

.user-add-form h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  color: var(--user-add-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.user-add-form .col-form-label {
  display: block;
  margin-bottom: 8px;
  padding: 0;
  color: var(--user-add-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.user-add-form .form-control {
  box-sizing: border-box;
  width: 100%;
  height: calc(2.25rem + 2px);
  padding: 0.375rem 0.75rem;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  color: var(--user-add-text);
  font-size: 15px;
  line-height: 1.5;
  -webkit-appearance: none;
  appearance: none;
}

.user-add-form .form-control:focus {
  border-color: var(--user-add-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.16);
  outline: none;
}

.user-add-form .form-group {
  margin-bottom: 16px;
}

.user-add-form ::v-deep .form-group .form-group {
  margin-bottom: 0;
}

.user-add-photo {
  margin: 16px 0 24px;
}

.user-add-photo .user-profile-photo {
  position: relative;
}

.user-add-photo__overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-add-photo__trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  appearance: none;
  -webkit-appearance: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.user-add-photo__trigger:active {
  transform: scale(0.98);
  opacity: 0.85;
}

.user-add-photo__trigger:focus-visible,
.user-add-save:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.28);
  outline-offset: 2px;
}

.user-add-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.user-add-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.user-add-checkbox label {
  margin: 0;
  color: var(--user-add-text);
  font-size: 14px;
  line-height: 1.4;
}

.user-add-form-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 32px 32px;
}

.user-add-save {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.user-add-save:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.user-add-save:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.user-add-page .uk-alert {
  margin: 16px;
}

.user-add-page .uk-alert p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.user-add-page ::v-deep .multiselect,
.user-add-page ::v-deep .custom-multiselect,
.user-add-page ::v-deep .corporate-select .multiselect {
  min-height: 40px;
  height: auto !important;
  overflow: visible;
  box-shadow: none !important;
}

.user-add-page ::v-deep .multiselect__tags,
.user-add-page ::v-deep .custom-multiselect .multiselect__tags,
.user-add-page ::v-deep .multiselect--active .multiselect__tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  box-sizing: border-box;
  min-height: 38px;
  padding: 8px 40px 8px 8px;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  box-shadow: none !important;
}

.user-add-page ::v-deep .multiselect--active .multiselect__tags {
  border-color: var(--user-add-primary);
}

.user-add-page ::v-deep .multiselect__select {
  height: 40px;
}

.user-add-page ::v-deep .multiselect__input,
.user-add-page ::v-deep .multiselect__single,
.user-add-page ::v-deep .multiselect__placeholder {
  margin-bottom: 0;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none !important;
  outline: none;
  font-size: 15px;
  line-height: 1.5;
  -webkit-appearance: none;
  appearance: none;
}

.user-add-page ::v-deep .multiselect__input:focus,
.user-add-page ::v-deep .multiselect__single:focus {
  background: transparent;
  border: 0;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  outline: none;
}

.user-add-page ::v-deep .corporate-select__locked {
  height: calc(2.25rem + 2px);
  padding: 0.375rem 0.75rem;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  font-size: 15px;
  line-height: 1.5;
}

.user-add-page ::v-deep .phone-input,
.user-add-page ::v-deep input[type="tel"] {
  font-size: 15px;
}

.user-add-page ::v-deep .vue-tel-input {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: calc(2.25rem + 2px);
  overflow: visible;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  background: #fff;
  box-shadow: none;
}

.user-add-page ::v-deep .vue-tel-input .vti__dropdown {
  min-width: 40px;
  min-height: calc(2.25rem + 2px);
  border: 0;
  background: transparent;
}

.user-add-page ::v-deep .vue-tel-input .vti__input,
.user-add-page ::v-deep .vue-tel-input input {
  min-height: calc(2.25rem + 2px);
  height: calc(2.25rem + 2px);
  padding: 0.375rem 0.75rem 0.375rem 8px;
  border: 0;
  background: transparent;
  box-shadow: none;
  font-size: 15px;
  line-height: 1.5;
}

.user-add-page ::v-deep .uk-alert-close {
  min-width: 44px;
  min-height: 44px;
}

.user-add-hr-panel p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

@media (max-width: 959px) {
  .user-add-page {
    box-sizing: border-box;
    max-width: 100%;
    margin: 0;
    padding: 0 0 24px;
  }

  .user-add-form h4 {
    font-size: 20px;
  }

  .user-add-page .uk-alert p,
  .user-add-hr-panel p {
    font-size: 16px;
  }

  .user-add-form .form-control {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .user-add-photo__trigger {
    width: 48px;
    height: 48px;
  }

  .user-add-checkbox {
    min-height: 48px;
  }

  .user-add-checkbox label {
    font-size: 16px;
  }

  .user-add-save {
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .user-add-page ::v-deep .multiselect,
  .user-add-page ::v-deep .custom-multiselect,
  .user-add-page ::v-deep .corporate-select .multiselect {
    min-height: 48px;
  }

  .user-add-page ::v-deep .multiselect__tags,
  .user-add-page ::v-deep .custom-multiselect .multiselect__tags,
  .user-add-page ::v-deep .multiselect--active .multiselect__tags {
    min-height: 48px;
  }

  .user-add-page ::v-deep .multiselect__select {
    height: 48px;
  }

  .user-add-page ::v-deep .multiselect__input,
  .user-add-page ::v-deep .multiselect__single,
  .user-add-page ::v-deep .multiselect__placeholder {
    font-size: 16px;
    line-height: 1.4;
  }

  .user-add-page ::v-deep .corporate-select__locked {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .user-add-page ::v-deep .phone-input,
  .user-add-page ::v-deep input[type="tel"] {
    min-height: 48px;
    font-size: 16px;
  }

  .user-add-page ::v-deep .vue-tel-input {
    min-height: 48px;
  }

  .user-add-page ::v-deep .vue-tel-input .vti__dropdown {
    min-width: 48px;
    min-height: 48px;
  }

  .user-add-page ::v-deep .vue-tel-input .vti__input,
  .user-add-page ::v-deep .vue-tel-input input {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px 12px 8px;
    font-size: 16px;
    line-height: 1.4;
  }

  .user-add-form-col + .user-add-form-col {
    border-top: 1px solid var(--user-add-border);
    border-left: 0;
  }

  .user-add-card {
    margin-top: 0 !important;
  }

  .user-add-form-col {
    padding: 16px;
  }

  .user-add-form .row {
    margin-right: 0;
    margin-left: 0;
  }

  .user-add-form .row > [class*="col-"] {
    padding-right: 0;
    padding-left: 0;
  }

  .user-add-form-footer {
    padding: 16px;
  }

  .user-add-save {
    width: 100%;
  }

  .user-add-photo .user-profile-photo,
  .user-profile-photo {
    width: 120px;
    height: 120px;
  }

  .user-add-hr-panel {
    min-height: 200px;
    padding: 24px 16px;
  }

  .user-add-page ::v-deep .user-from-excel-uploader .card {
    margin-top: 0 !important;
    overflow: hidden;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .user-add-page ::v-deep .user-from-excel-uploader .uk-padding-large,
  .user-add-page ::v-deep .user-from-excel-uploader .uk-width-expand {
    padding: 16px !important;
  }

  .user-add-page ::v-deep .user-from-excel-uploader .uk-width-expand {
    border-top: 1px solid var(--user-add-border);
    border-left: 0 !important;
  }
}

@media (hover: hover) and (pointer: fine) {
  .user-add-save:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }

  .user-add-photo__trigger:hover {
    background: #fff;
    cursor: pointer;
  }

  input[type="file"],
  input[type="file"]::-webkit-file-upload-button {
    cursor: pointer;
  }
}

@media (prefers-reduced-motion: reduce) {
  .user-add-save:active:not(:disabled),
  .user-add-photo__trigger:active {
    transform: none;
  }
}
</style>

<style>
.multiselect__tag {
  color: white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #3e416d;
}

.multiselect__tag-icon {
  background-color: transparent;
  line-height: 2.2;
}

.multiselect__tag-icon:after {
  color: #fff;
}

.multiselect__tag-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.multiselect__input {
  border: unset !important;
}

.multiselect__tags .multiselect__input {
  border: unset !important;
}
</style>
